import { Pressable } from "react-native";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../common/shadcn/tooltip";
import { View } from "react-native";
import StyledText from "../common/ui/styled-text";
import { ReactNode } from "react";
import { Loader, LucideIcon } from "lucide-react-native";

interface ActionButtonProps {
  label?: string;
  icon?: LucideIcon;
  iconRight?: LucideIcon;
  customIcon?: ReactNode;
  variant?:
    | "default"
    | "primary"
    | "danger"
    | "outline"
    | "black"
    | "success"
    | "ghost";
  tooltip?: string;
  onPress?: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  loadingLabel?: string;
  iconFill?: string;
  textColorOverride?: string;
  size?: "default" | "small";
  className?: string;
  hoverBgOverride?: string;
}

export function ActionButton({
  label,
  icon: Icon,
  iconRight: IconRight,
  customIcon,
  variant = "default",
  tooltip,
  onPress,
  isLoading,
  disabled,
  loadingLabel,
  iconFill,
  textColorOverride,
  size = "default",
  className,
  hoverBgOverride,
}: ActionButtonProps) {
  const variants = {
    default: `bg-gray-100 ${
      hoverBgOverride
        ? hoverBgOverride
        : isLoading
          ? "hover:bg-gray-100"
          : "hover:bg-gray-200"
    } border-gray-200`,
    primary: `bg-blue-500 ${
      hoverBgOverride
        ? hoverBgOverride
        : isLoading
          ? "hover:bg-blue-500"
          : "hover:bg-blue-600"
    } border-0`,
    black: `bg-black ${
      hoverBgOverride
        ? hoverBgOverride
        : isLoading
          ? "hover:bg-black"
          : "hover:bg-gray-900"
    } border-0`,
    danger: `bg-red-500 ${
      hoverBgOverride
        ? hoverBgOverride
        : isLoading
          ? "hover:bg-red-500"
          : "hover:bg-red-600"
    } border-red-600`,
    outline: `bg-white border border-gray-200 ${
      hoverBgOverride
        ? hoverBgOverride
        : isLoading
          ? "hover:bg-white"
          : "hover:bg-gray-100"
    }`,
    success: `bg-green-600 ${
      hoverBgOverride
        ? hoverBgOverride
        : isLoading
          ? "hover:bg-green-600"
          : "hover:bg-green-700"
    } border-green-600`,
    ghost: `bg-transparent ${hoverBgOverride ? hoverBgOverride : ""}`,
  };

  const textColors = {
    default: "text-black",
    primary: "text-white",
    black: "text-white",
    danger: "text-white",
    outline: "text-gray-900",
    success: "text-white",
    ghost: "text-black",
  };

  const iconColors = {
    default: "black",
    primary: "white",
    black: "white",
    danger: "white",
    // outline: "#030712",
    outline: "#1f2937",
    success: "white",
    ghost: "black",
  };

  const actionButton = (
    <Pressable
      onPress={onPress}
      disabled={isLoading || disabled}
      className={`grid grid-cols-1 grid-rows-1 rounded-md h-10 ${
        size === "default" ? "px-4 py-2" : "px-3 py-1"
      } ${variants[variant]} ${
        isLoading
          ? "cursor-default"
          : disabled
            ? "cursor-not-allowed"
            : "cursor-pointer"
      } ${disabled || isLoading ? "opacity-80" : ""} ${className}`}
    >
      <View
        className={`col-start-1 row-start-1 inline-flex flex-row items-center justify-center gap-2 ${
          isLoading ? "opacity-0" : "opacity-100"
        }`}
      >
        {customIcon ? (
          customIcon
        ) : Icon ? (
          <Icon size={16} color={iconFill || iconColors[variant]} />
        ) : null}
        {label && (
          <StyledText
            className={`text-sm font-medium ${
              textColorOverride || textColors[variant]
            }`}
          >
            {label}
          </StyledText>
        )}
        {IconRight && <IconRight size={16} color={iconColors[variant]} />}
      </View>

      <View
        className={`col-start-1 row-start-1 flex-row items-center justify-center gap-2 ${
          isLoading ? "visible" : "hidden"
        }`}
      >
        <Loader
          size={16}
          color={iconColors[variant]}
          className="animate-spin [transform-origin:center]"
        />
        <StyledText className={`text-sm font-medium ${textColors[variant]}`}>
          {loadingLabel || "Loading..."}
        </StyledText>
      </View>
    </Pressable>
  );

  if (tooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{actionButton}</TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return actionButton;
}
